import type { ReactNode } from 'react';
import { Link } from '@customerio/pluma-components/react';
import type { SupportRequestKind } from '../types';
import { GOAL_PLACEHOLDER } from './form-schema';

/**
 * Everything the support form varies by request kind. Presets supply a
 * `Partial`, and `supportFormCopy` fills the gaps from the default.
 */
export interface SupportFormCopy {
  title: string;
  /** The field's accessible name, shown as a visible label unless hidden below. */
  goalLabel: string;
  /** Hides the visible label; `goalLabel` travels as `aria-label` instead. */
  isGoalLabelHidden: boolean;
  /** Helper text under the box. A node, so a preset can link out of it. */
  goalDescription: ReactNode;
  goalPlaceholder: string;
  /** Whether the optional "Share a video" field is offered. */
  hasVideoField: boolean;
  submitLabel: string;
  successMessage: string;
}

/** What the form says when it is opened as a plain "I need help" request. */
export const DEFAULT_SUPPORT_FORM_COPY: SupportFormCopy = {
  title: 'Describe your support request',
  goalLabel: 'Describe your problem',
  isGoalLabelHidden: false,
  goalDescription: 'The more detail you can share the better we can help.',
  // The canonical one: isDefaultGoalText and the Zod refine both compare
  // against it, so it must not be forked here.
  goalPlaceholder: GOAL_PLACEHOLDER,
  hasVideoField: true,
  submitLabel: 'Send request',
  successMessage: "Your support request has been submitted. We'll get back to you soon.",
};

/**
 * Copy keyed by request kind, rather than per-field overrides at the call site.
 *
 * A caller picks a named shape and gets wording that has been written and
 * reviewed once. Letting each call site pass its own title and labels would let
 * the same form drift into a different voice everywhere it is opened.
 */
const PRESETS = {
  verification_reply: {
    title: 'Reply to verify your account',
    // Hidden: the title and placeholder already say what the box is for. Kept
    // as the accessible name so a screen reader still hears one.
    goalLabel: 'Your reply',
    isGoalLabelHidden: true,
    goalDescription: (
      <>
        Answer the questions in our verification request. We verify accounts against our{' '}
        <Link href="https://customer.io/legal/acceptable-use-policy" variant="secondary" isExternal={true}>
          Acceptable Use Policy
        </Link>
        .
      </>
    ),
    // A noun phrase, per the content guidelines: the description instructs.
    goalPlaceholder: 'Your reply',
    // Nothing to film: the ask is a question about the business, not a bug.
    hasVideoField: false,
    submitLabel: 'Send reply',
    successMessage: "Your reply has been sent. We'll pick the review back up.",
  },
  ai_endpoint_change: {
    title: 'Request an AI endpoint change',
    goalLabel: 'Your request',
    goalDescription:
      'Tell us which endpoint you want to use and why. Your AI endpoint is locked, so support makes the change.',
    goalPlaceholder: 'Your request',
    hasVideoField: false,
    successMessage: "Your request has been sent. We'll be in touch about your AI endpoint.",
  },
  sending_domain_disabled: {
    title: 'Ask about a disabled sending domain',
    goalLabel: 'Your message',
    goalDescription:
      "Tell us what you send from this domain and anything you've changed recently. Our deliverability team reviews it before sending can be re-enabled.",
    goalPlaceholder: 'Your message',
    hasVideoField: false,
    successMessage: "Your message has been sent. We'll be in touch about your sending domain.",
  },
  ip_pool_request_change: {
    title: 'Change your IP pool request',
    goalLabel: 'Your request',
    goalDescription:
      "Tell us what you'd like to change: a different pool, your expected sending volume, or canceling the request.",
    goalPlaceholder: 'Your request',
    hasVideoField: false,
    successMessage: "Your request has been sent. We'll be in touch about your IP pool request.",
  },
} satisfies Record<SupportRequestKind, Partial<SupportFormCopy>>;

/** The copy for a kind, with anything it does not override filled from the default. */
export function supportFormCopy(kind: SupportRequestKind | undefined): SupportFormCopy {
  if (kind == null) return DEFAULT_SUPPORT_FORM_COPY;
  return { ...DEFAULT_SUPPORT_FORM_COPY, ...PRESETS[kind] };
}
